import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import cls from './menu.module.scss'
import MaterialIcon from '~src/new/shared/ui/MaterialIcon/MaterialIcon'
import { useLocalStorageValue } from '~src/new/shared/lib/useLocalStorageValue'
import { SvgIcon } from '~src/new/shared/ui/SvgIcon/SvgIcon'
import { useApi } from '~shared/api/ApiContext'
import { Link } from 'react-router-dom'
import { useWidth } from '@/src/app/providers/WidthProvider'

export const SIDEBAR_WIDTH_COLLAPSED = 80
export const SIDEBAR_WIDTH_EXPANDED = 280

const normalizePath = value => {
  if (!value) return ''
  const trimmed = value.endsWith('/') && value !== '/' ? value.slice(0, -1) : value
  return trimmed
}

const isPathActive = (currentPath, link) => {
  if (!link) return false
  const normalizedLink = normalizePath(link)
  const normalizedCurrent = normalizePath(currentPath)
  const isRootLink = normalizedLink === '/'
  return (
    normalizedCurrent === normalizedLink ||
    (!isRootLink && normalizedCurrent?.startsWith(`${normalizedLink}/`)) ||
    (normalizedLink === '/dashboard' && normalizedCurrent === '/')
  )
}

const MenuIcon = ({ label, icon, specialIcon }) => {
  if (specialIcon) {
    return (
      <SvgIcon icon={specialIcon} name={label} size={24} className={cls.icon} ariaLabel={label} />
    )
  }
  return <MaterialIcon name={icon} size={24} className={cls.icon} ariaLabel={label} isSymbol />
}

const MenuItem = ({ item, isActive, onItemClick, isCollapsed, style, className }) => {
  const { id, badge, icon, specialIcon, label, to, showSlim, title } = item
  const Container = to ? Link : 'button'

  const badgeElement = useMemo(() => !!badge && <span className={cls.badge}>{badge}</span>, [badge])

  return (
    <Container
      {...(to ? { to } : { type: 'button' })}
      className={classNames(cls.item, className, {
        [cls.itemActive]: isActive,
        [cls.notSlimItem]: !showSlim,
      })}
      style={style}
      onClick={e => {
        e.stopPropagation()
        if (!to) e.preventDefault()
        onItemClick?.()
      }}
      aria-current={isActive ? 'page' : undefined}
      aria-label={isCollapsed ? label : undefined}
      title={isCollapsed ? label : undefined}
      key={id || to || `${title}`}
    >
      {showSlim ? (
        <div className={cls.iconWrapper}>
          <MenuIcon label={label} icon={icon} specialIcon={specialIcon} />
        </div>
      ) : null}
      <span className={cls.label}>
        {label} {badgeElement}
      </span>
    </Container>
  )
}

/**
 * Меню с раскрытием (иконки -> иконки + подписи) и группами (chevron right/down).
 * items поддерживают вложенные пункты через children.
 */
export const Menu = ({
  className,
  style,
  items = [],
  activePath,
  collapsed: collapsedProp,
  onCollapsedChange,
  onItemClick,
  collapsedWidth = SIDEBAR_WIDTH_COLLAPSED,
  expandedWidth = SIDEBAR_WIDTH_EXPANDED,
  persistKey = 'new-sidebar-collapsed',
}) => {
  const api = useApi()
  const { isMobile } = useWidth()
  const currentPath = activePath ?? (typeof window !== 'undefined' ? window.location?.pathname : '')

  const { value: persistedCollapsed, setValue: setPersistedCollapsed } = useLocalStorageValue(
    persistKey,
    '0',
  )

  const isControlled = collapsedProp !== undefined
  const collapsed = isMobile ? false : isControlled ? !!collapsedProp : persistedCollapsed === '1'

  const setCollapsed = useCallback(
    next => {
      const nextBool = !!next
      if (!isControlled) setPersistedCollapsed(nextBool ? '1' : '0')
      onCollapsedChange?.(nextBool)
    },
    [isControlled, onCollapsedChange, setPersistedCollapsed],
  )

  const resolvedWidth = collapsed ? collapsedWidth : expandedWidth

  const renderedItems = useMemo(() => {
    return items.map(item => {
      const isActive = isPathActive(currentPath, item?.to)

      return (
        <MenuItem
          key={item.id || item.to || `${item.title}`}
          item={item}
          isActive={isActive}
          onItemClick={onItemClick}
          isCollapsed={collapsed}
        />
      )
    })
  }, [currentPath, items, onItemClick, collapsed])

  const handleLogout = useCallback(() => {
    api.logout()
  }, [api])

  return (
    <aside
      className={classNames(cls.sidebar, className, {
        [cls.sidebarCollapsed]: collapsed,
      })}
      style={{
        '--sidebar-width': `${resolvedWidth}px`,
        ...style,
      }}
      aria-label="Сайдбар"
      id="sidebar"
    >
      {!isMobile && (
        <button
          type="button"
          className={cls.toggleButton}
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Развернуть сайдбар' : 'Свернуть сайдбар'}
        >
          <MaterialIcon
            name={collapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
          />
        </button>
      )}
      <div className={cls.content}>
        <nav className={cls.items} aria-label="Навигация">
          {renderedItems}
        </nav>

        <div className={cls.footer}>
          <MenuItem
            item={{
              label: 'Выйти',
              icon: 'logout',
              showSlim: true,
            }}
            className={cls.logoutItem}
            isActive={false}
            onItemClick={handleLogout}
          />
        </div>
      </div>
    </aside>
  )
}

Menu.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  activePath: PropTypes.string,
  onNavigate: PropTypes.func,
  collapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  topOffset: PropTypes.number,
  bottomOffset: PropTypes.number,
  collapsedWidth: PropTypes.number,
  expandedWidth: PropTypes.number,
  persistKey: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      showSlim: PropTypes.bool,
      icon: PropTypes.string,
      to: PropTypes.string,
    }),
  ),
}

export default Menu
